import { formatDateForInput } from 'utilities/common';

/**
 * Keeps an end date from falling before its start date in a form.
 *
 * withDateChronology tags each end-date field with the start-date field it follows (`minDateKey`). A date input that
 * spreads getDateChronologyInputProps then takes the start date as its native `min`, which greys out the earlier days
 * in the picker and stops the form from submitting, and explains the problem under the field. A pair is only checked
 * once both of its dates are filled in.
 *
 *   const summaries = withDateChronology(baseSummaries, [['tripStartDate', 'tripEndDate']]);
 *   <TextField type="date" ... {...getDateChronologyInputProps(summary, values, summaries)} />
 *
 * See EditableCard and CreateTripModal for examples of how these props are used.
 */

// True when `date` falls on an earlier calendar day than `minDate`.
export const isDateBefore = (date, minDate) => {
  const day = formatDateForInput(date);
  const minDay = formatDateForInput(minDate);
  return !!day && !!minDay && day < minDay;
};

export const withDateChronology = (fields, datePairs) =>
  fields.map(field => {
    const pair = datePairs.find(([, endKey]) => endKey === field.accessorKey);
    return pair ? { ...field, minDateKey: pair[0] } : field;
  });

export const getDateChronologyInputProps = (field, values, fields) => {
  const minDate = field.minDateKey ? formatDateForInput(values[field.minDateKey]) : '';
  if (!minDate) return {};

  const isOutOfOrder = isDateBefore(values[field.accessorKey], minDate);
  const startHeader = fields.find(f => f.accessorKey === field.minDateKey)?.header ?? 'the start date';

  return {
    inputProps: { min: minDate },
    error: isOutOfOrder,
    helperText: isOutOfOrder ? `Must be on or after ${startHeader}` : undefined,
  };
};
